import { filter } from '@atlaskit/adf-utils/traverse';

const EXTENSION_TYPES = new Set(['extension', 'bodiedExtension', 'inlineExtension']);

interface MacroContentNode {
	type: string;
	attrs?: {
		extensionKey?: string;
		parameters?: {
			macroParams?: Record<string, { value: string }>;
		};
	};
}

/**
 * Finds all extension nodes matching a given extensionKey from raw content nodes.
 *
 * Handles resolving contentNodes (which may be a promise), validating the body
 * structure, parsing the ADF JSON, and filtering for extension nodes of type
 * 'extension', 'bodiedExtension', or 'inlineExtension' whose `attrs.extensionKey`
 * matches the provided key.
 *
 * Returns an empty array if contentNodes is invalid, the representation is not
 * 'atlas_doc_format', or the ADF JSON cannot be parsed.
 */
export const findMacroContentNodes = async (
	contentNodes: any,
	extensionKey: string,
): Promise<MacroContentNode[]> => {
	const resolvedNodes =
		contentNodes && typeof contentNodes.then === 'function' ? await contentNodes : contentNodes;

	if (!resolvedNodes?.[0]?.body?.dynamic?.value) {
		return [];
	}

	if (resolvedNodes[0].body.dynamic.representation !== 'atlas_doc_format') {
		return [];
	}

	let documentADF;
	try {
		documentADF = JSON.parse(resolvedNodes[0].body.dynamic.value);
	} catch {
		return [];
	}

	return filter(documentADF, (node) => {
		return EXTENSION_TYPES.has(node.type) && node.attrs?.extensionKey === extensionKey;
	}) as MacroContentNode[];
};

export type { MacroContentNode };
